import { css } from 'lit'

// Widget-internal chrome theme — not part of the API contract.
export type Theme = 'light' | 'dark'

// 每個 IP 的前端色票（P1 起由 registry 提供；本 phase 由 caller 從後端 palette 映射）。
export interface IpPalette {
  theme: Theme
  brand: string // CSS-valid hex
}

// Token 結構鏡射 Figma variables（file 5O7ZqKz8BHiTjO2GYZurgn）三層 collections：
//   Color/Global（neu/* primitives）→ 直接當值寫進下面的 light/dark 兩套 semantic；
//   Color/IP（每 IP 四階：default / weak 80% / subtle 50% / soft 10%）→ --mp-brand{,-weak,-subtle,-soft}，
//     由 applyPalette 從單一 brand hex 推導（Figma 的 IP mode 切換 = 這裡換 brand 值）；
//   Color/Semantic → --mp-<group>-<role>，group 對齊 Figma 第一段（bg / text / border / button→btn）。
// token ↔ Figma variable 對照（省略者同名）：
//   --mp-bg-base=bg/base · --mp-scrim-soft=bg/scrim--soft · --mp-btn-hover=button/overlay/hover ·
//   --mp-btn-pressed=button/overlay/pressed · --mp-btn-disabled=button/color/disabled ·
//   --mp-alert=border/error · --mp-brand=IP default（=bg/primary=button/color/default）·
//   --mp-brand-weak=IP weak（=bg/secondary=border/focus）· --mp-brand-soft=IP soft（=bg/focus）。
//   --mp-track=Answer Result（303:2010）解析出的 bg-surface #f2f2f2（該元件把 surface 綁到
//     bg/Soft）；它在 Figma 沒有自己的語意名，所以這裡列出取值的節點而非變數名。
// 兩個容易誤認的對照（2026-09-01 對五個 IP section 逐項核對過，當時全部一致）：
//   ⚠️ --mp-track 與 Figma 的 bg-progress-track **無關**。後者仍在 Figma、且各 IP 不同值
//      （News #5857574d / Cars #c8c8c84d），但 SDK 沒有任何 token 取自它：頁面進度條的軌道
//      走 --mp-scrim-soft，與 Progress Bar 元件（298:2320）綁的 bg-scrim 一致。
//   ⚠️ border/error 只綁在元件層的狀態集（50:33 文字框 / 133:735 下拉），五個 IP 的畫面都
//      沒有畫過錯誤狀態，所以它不隨 IP mode 變 —— 深色底的 Cars 因此沒有專屬的警示色。
// Self-contained in the widget's Shadow DOM — no Tailwind, no coupling to admin.
export const themeStyles = css`
  :host {
    /* bg */
    --mp-bg-base: #fff;
    --mp-scrim-soft: #4d4d4d4d;
    /* 結果 bar 軌道（Figma Results 的 track = neu/100；對應 bg/Soft，dark = neu/800）。 */
    --mp-track: #f2f2f2;
    /* text（icon 沿用同名 text token；Figma icon/* 各 mode 值與 text/* 一致） */
    --mp-text-primary: #1a1a1a;
    --mp-text-secondary: #4d4d4d;
    --mp-text-placeholder: #999999;
    --mp-text-meta: #999999;
    --mp-text-muted: #b2b2b2;
    --mp-text-inverse: #fff;
    /* border */
    --mp-border-default: #999999;
    --mp-border-sub: #d9d9d9;
    /* button */
    --mp-btn-hover: #66666640;
    --mp-btn-pressed: #6666661a;
    --mp-btn-disabled: #d9d9d9;
    /* global */
    --mp-alert: #dd1033;
    /* brand（IP 四階；default 值 = News，applyPalette 依 IP 覆寫） */
    --mp-brand: #006cfe;
    --mp-brand-weak: #006cfecc;
    --mp-brand-subtle: #006cfe80;
    --mp-brand-soft: #006cfe1a;
    /* 遮蔽帶的漸層起點（Figma bg/overlay-soft）：底色的 20% alpha。搭配 --mp-bg-base
       （= bg/overlay-weak）組成 action bar 與收合卡 CTA 的背景。起點刻意不是 transparent
       —— 那會讓帶子的上緣完全看不出遮蔽，捲到底下的選項整段透出來。 */
    --mp-overlay-soft: #ffffff33;
    /* 數字字體（Figma Type/Number/*：Open Sans）。題號 Q1 與進度頁碼 1/6 走這條，正文仍是
       Noto Sans TC。widget 不載入 web font（嵌在第三方頁面，多一個字型請求、且宿主 CSP 可能
       擋掉），所以只有宿主端已有 Open Sans 才會生效，否則退回下面同一串正文 stack ——
       字級與字重照設計稿走，字形本身是漸進增強。 */
    --mp-font-number: 'Open Sans', 'Noto Sans TC', 'PingFang TC', system-ui, sans-serif;

    display: block;

    /* Host-page CSS isolation. Shadow DOM blocks the host's *selectors* from reaching
       widget internals, but *inherited* properties still cross the boundary from the
       (uncontrolled) host element's computed style. Neutralise the inherited typographic
       properties here so an IP site's body typography can't bleed in. All widget sizing
       is px (no rem/em) so the host root font-size can't scale us either.

       !important: a host rule that targets our element directly — e.g. a star-selector
       "color: red !important" or a pulse-survey type rule — matches the host element
       in light DOM and would otherwise beat a plain :host declaration (lower specificity),
       then inherit inward. Marking the reset !important lets :host (specificity 0,1,0) win
       over those. Custom props (--mp-*) and display are intentionally left plain: --mp-brand
       must stay overridable by the inline palette, and display is the host's to control.
       See docs/widget-isolation.md. */
    font-family: 'Noto Sans TC', 'PingFang TC', system-ui, sans-serif !important;
    font-size: 16px !important;
    font-weight: 400 !important;
    font-style: normal !important;
    line-height: 1.5 !important;
    letter-spacing: normal !important;
    word-spacing: normal !important;
    text-align: start !important;
    text-transform: none !important;
    text-indent: 0 !important;
    white-space: normal !important;
    direction: ltr !important;
    color: var(--mp-text-primary) !important;
  }

  :host([theme='dark']) {
    /* 值對齊 Figma Color/Semantic 的 Cars mode（唯一 dark IP）。
       --mp-btn-hover / --mp-btn-pressed 半透明、light/dark 共用，刻意不覆寫；
       --mp-text-placeholder / --mp-text-meta 兩 mode 同為 neu/400，也不覆寫。 */
    --mp-bg-base: #333333;
    --mp-track: #333333;
    --mp-scrim-soft: #d9d9d94d;
    --mp-text-primary: #fff;
    --mp-text-secondary: #b2b2b2;
    --mp-text-muted: #666666;
    --mp-text-inverse: #1a1a1a;
    --mp-border-default: #666666;
    --mp-border-sub: #4d4d4d;
    --mp-btn-disabled: #333333;
    --mp-overlay-soft: #33333333;
  }
`

// Only CSS-valid hex lengths (3/4/6/8). Rejects 5/7-char (would render black/transparent)
// and any non-hex (guards CSS injection, even though the value is first-party).
const HEX = /^#(?:[0-9a-f]{3,4}|[0-9a-f]{6}|[0-9a-f]{8})$/i

// 品牌色 alpha 變體，對齊 Figma Color/IP 的四階：weak 80%（cc）/ subtle 50%（80）/ soft 10%（1a）。
// Safari 16 沒有 color-mix()，故在 JS 補：6 碼 brand（含 '#' 共 7 字元）→ 8 碼補 alpha；
// 已帶 alpha（4/8 碼）的 brand 直接沿用；3 碼簡寫（如 #abc）不補 alpha、變體等同 brand
// （實務輸入皆 6 碼，不影響）。
function alphaHex(brand: string, alpha: string): string {
  return brand.length === 7 ? `${brand}${alpha}` : brand
}

// Apply the IP palette to the host: sets the `theme` attribute (drives the
// :host([theme='dark']) neutral set) and the brand custom properties. setProperty is
// idempotent and preserves existing inline styles. Invalid brand → theme set, brand untouched
// (CSS default --mp-brand stays).
export function applyPalette(host: HTMLElement, palette: IpPalette): void {
  host.setAttribute('theme', palette.theme)
  if (!HEX.test(palette.brand))
    return
  host.style.setProperty('--mp-brand', palette.brand)
  host.style.setProperty('--mp-brand-weak', alphaHex(palette.brand, 'cc'))
  host.style.setProperty('--mp-brand-subtle', alphaHex(palette.brand, '80'))
  host.style.setProperty('--mp-brand-soft', alphaHex(palette.brand, '1a'))
}
